/**
 * Utilidades de formateo de fechas para el frontend.
 * Formato esperado por defecto: dd/mm/yyyy
 * También soporta ISO 8601 (fecha_sesion del payload de eventos).
 */

const MESES_CORTO = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const MESES_MINUSCULA = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DIAS_SEMANA = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];

/**
 * Extrae el día (dd) de una fecha en formato dd/mm/yyyy
 */
export const getDay = fecha => {
    if (!fecha) return '--';
    const parts = fecha.split('/');
    return parts[0] || '--';
};

/**
 * Extrae el mes abreviado (ENE, FEB, etc.) de una fecha en formato dd/mm/yyyy
 */
export const getMonth = fecha => {
    if (!fecha) return '---';
    const parts = fecha.split('/');
    const monthNum = parseInt(parts[1], 10);
    return MESES_CORTO[monthNum - 1] || '---';
};

/**
 * Devuelve el nombre del día de la semana (DOMINGO, LUNES, etc.) de una fecha en formato dd/mm/yyyy
 */
export const getDayName = fecha => {
    if (!fecha) return '';
    const parts = fecha.split('/');
    if (parts.length !== 3) return '';
    const date = new Date(parts[2], parts[1] - 1, parts[0]);
    return DIAS_SEMANA[date.getDay()] || '';
};

/**
 * Formatea una fecha "dd/mm/yyyy HH:mm" a "dd MMM, yyyy • HH:mm h"
 * Ejemplo: "31/05/2026 12:00" → "31 may, 2026 • 12:00 h"
 */
export const formatEventDate = fecha => {
    if (!fecha) return '';
    const [datePart, timePart] = fecha.split(' ');
    const parts = datePart?.split('/');
    if (!parts || parts.length < 3) return fecha;

    const day = parts[0];
    const monthIndex = parseInt(parts[1], 10) - 1;
    const year = parts[2];
    const month = MESES_MINUSCULA[monthIndex] || '---';

    if (timePart) {
        return `${day} ${month}, ${year} • ${timePart} h`;
    }
    return `${day} ${month}, ${year}`;
};

/**
 * Normaliza una cadena de fecha al formato ISO que acepta `new Date()`.
 */
export const normalizeFechaString = str => {
    // Detectar "dd/mm/yyyy[ HH:mm]"
    const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}:\d{2}))?$/.exec(str);
    if (ddmmyyyy) {
        const [, dd, mm, yyyy, time] = ddmmyyyy;
        return time ? `${yyyy}-${mm}-${dd}T${time}` : `${yyyy}-${mm}-${dd}`;
    }
    return str;
};

/**
 * Parsea fecha de sesión del payload de la API.
 */
export const parseFechaSesion = isoString => {
    if (!isoString) return { mes: '', dia: '', hora: '' };
    try {
        const d = new Date(normalizeFechaString(isoString));
        if (isNaN(d.getTime())) return { mes: '', dia: '', hora: '' };
        const mes = MESES_CORTO[d.getMonth()] ?? '';
        const dia = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const hora = `${hh}:${mm}`;
        return { mes, dia, hora: hora === '00:00' ? '' : hora };
    } catch {
        return { mes: '', dia: '', hora: '' };
    }
};

/**
 * Convierte una fecha de DD/MM/YYYY a YYYY-MM-DD (formato HTML5 input type="date").
 * Si ya está en YYYY-MM-DD o el input no es válido, la devuelve tal cual.
 *
 * @param {string} fecha
 * @returns {string}
 */
export const toDateInputFormat = fecha => {
    if (!fecha) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return fecha;
    // normalizeFechaString maneja DD/MM/YYYY → YYYY-MM-DD
    const normalized = normalizeFechaString(fecha);
    // Si normalizeFechaString agregó hora (T00:00), quedarnos solo con la fecha
    return normalized.split('T')[0] || normalized;
};

/**
 * Formatea una fecha al formato DD/MM/YYYY (sin hora).
 *
 * Acepta tanto el formato que devuelve la API (dd/mm/yyyy) como ISO 8601.
 * Si el valor no es una fecha reconocible, lo devuelve tal cual para no
 * mostrar "Invalid Date" en la interfaz.
 *
 * @param {string|null|undefined} fecha
 * @returns {string}
 */
export const formatDateOnly = fecha => {
    if (!fecha) return '-';
    if (typeof fecha !== 'string') return String(fecha);

    // La API devuelve dd/mm/yyyy: se devuelve tal cual, sin pasar por Date,
    // para evitar corrimientos de día por zona horaria.
    const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(fecha);
    if (ddmmyyyy) return fecha;

    const iso = normalizeFechaString(fecha).split('T')[0];
    const isoParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (isoParts) {
        const [, year, month, day] = isoParts;
        return `${day}/${month}/${year}`;
    }

    return fecha;
};

/**
 * Calcula la edad a partir de una fecha de nacimiento en formato YYYY-MM-DD.
 *
 * @param {string} fechaNacimiento  Fecha en formato YYYY-MM-DD
 * @returns {number} Edad en años, o 0 si la fecha es inválida
 */
export const calcularEdad = fechaNacimiento => {
    if (!fechaNacimiento) return 0;
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    if (isNaN(nacimiento.getTime())) return 0;
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
        edad--;
    }
    return Math.max(0, edad);
};

/**
 * Convierte un Date (o string ISO) al formato "YYYY-MM-DDTHH:mm" que acepta
 * <input type="datetime-local"> y los props `min`/`max` de BaseDateTimeInput.
 * Devuelve null si el valor es null/undefined.
 */
export const toDateTimeInputFormat = value => {
    if (!value) return null;
    const str = value instanceof Date ? value.toISOString() : String(value);
    return str.replace(/[+-]\d{2}:\d{2}$/, '').replace(/Z$/, '').slice(0, 16);
};

/**
 * Normaliza un valor de fecha heterogéneo al formato "YYYY-MM-DDTHH:mm" que
 * consumen los pickers de admin y `<input type="datetime-local">`.
 *
 * - Recorta el sufijo de zona ("+02:00", "Z") y los segundos.
 * - Cambia el separador de espacio por "T" (el backend envía "Y-m-d H:i:s").
 * - Con un `Date`, usa su representación UTC, igual que toDateTimeInputFormat.
 *
 * Devuelve '' si no hay valor.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export const toDateTimeInputValue = value => {
    if (value == null) return '';
    const str = value instanceof Date ? value.toISOString() : String(value).trim();
    if (!str) return '';
    return str
        .replace(/[+-]\d{2}:\d{2}$/, '')
        .replace(/Z$/, '')
        .replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T')
        .slice(0, 16);
};

/**
 * Formatea "YYYY-MM-DDTHH:mm" (o "YYYY-MM-DD HH:mm[:ss]") como
 * "DD/MM/YYYY HH:mm", el texto que muestran los pickers de admin y las
 * ayudas de rango. Devuelve '' si no hay valor.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export const formatDateTimeLabel = value => {
    const normalized = toDateTimeInputValue(value);
    if (!normalized) return '';
    const [date, time] = normalized.split('T');
    return `${formatDateOnly(date)} ${time}`;
};

/**
 * Normaliza un valor de fecha heterogéneo al formato "YYYY-MM-DDTHH:mm" que
 * consume <input type="datetime-local"> (y BaseDateTimeInput).
 *
 * A diferencia de toDateTimeInputFormat —que solo recorta el sufijo de zona y
 * sirve para fechas que ya vienen en hora de pared con offset (los eventos)—
 * aquí SÍ convertimos los instantes absolutos:
 *   - "...Z" o "...+HH:MM": es un instante real (p. ej. `fecha_zulu`, que el
 *     backend emite con toIso8601ZuluString). Se convierte a la hora local del
 *     navegador, que es la zona de los usuarios (Europe/Madrid), igual que
 *     hacía `moment(...).format(...)` en el formulario legacy.
 *   - "YYYY-MM-DD HH:mm[:ss]" o ISO sin zona: ya es hora de pared; se conserva
 *     tal cual (evita corrimientos al re-guardar).
 *
 * Devuelve '' si no hay valor o no es parseable.
 */
export const toLocalDateTimeInput = value => {
    if (value == null || value === '') return '';
    const str = String(value).trim();
    if (!str) return '';

    const isAbsoluteInstant = /[zZ]$/.test(str) || /[+-]\d{2}:?\d{2}$/.test(str);
    if (isAbsoluteInstant) {
        const d = new Date(str);
        if (isNaN(d.getTime())) return '';
        const pad = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(str);
    return m ? `${m[1]}T${m[2]}` : '';
};

/**
 * Convierte "YYYY-MM-DDTHH:mm" (o "YYYY-MM-DD HH:mm[:ss]") al formato
 * "YYYY-MM-DD HH:mm:ss" que valida el backend (`date_format: "Y-m-d H:i:s"`).
 * Devuelve '' si no hay valor o no es parseable.
 */
export const toBackendDateTime = value => {
    if (value == null || value === '') return '';
    const str = String(value).trim().replace('T', ' ');
    const m = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2})(?::(\d{2}))?/.exec(str);
    if (!m) return '';
    return `${m[1]}:${m[2] || '00'}`;
};

/**
 * Formatea un instante ISO 8601 (p. ej. `start_at` de la API v2) a un texto
 * local legible para la UI: "DD/MM/YYYY a las HH:mm" en la zona del navegador.
 *
 * El v2 ya no preformatea la fecha (antes v1 enviaba "08/10/2026 a las 20:30"),
 * así que el front la convierte con Intl y la zona del usuario. Se mantiene el
 * formato histórico de v1 a propósito, para no cambiar lo que ve el usuario.
 *
 * Devuelve '' si no hay valor o no es parseable (nunca "Invalid Date").
 *
 * @param {string|null|undefined} value Instante ISO 8601.
 * @returns {string}
 */
export const formatSesionFecha = value => {
    if (value == null || value === '') return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const p = n => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} a las ${p(d.getHours())}:${p(d.getMinutes())}`;
};
